import { supabase } from '@/lib/supabase';
import { Character, CreateCharacterForm, ApiResponse } from '@/types/database';

export const charactersApi = {
  // Get all characters for the current user
  async getUserCharacters(): Promise<ApiResponse<Character[]>> {
    try {
      const { data, error } = await supabase
        .from('characters')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: data || [],
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch characters',
        success: false
      };
    }
  },

  // Get a single character by ID
  async getCharacter(id: string): Promise<ApiResponse<Character>> {
    try {
      const { data, error } = await supabase
        .from('characters')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch character',
        success: false
      };
    }
  },

  // Create a new character
  async createCharacter(characterData: CreateCharacterForm): Promise<ApiResponse<Character>> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Check subscription limits first
      const { data: usage } = await supabase
        .rpc('get_user_usage', { p_user_id: user.id });

      if (usage && usage[0]) {
        const currentUsage = usage[0];
        if (currentUsage.characters_count >= currentUsage.max_characters) {
          return {
            error: `You've reached your character limit (${currentUsage.max_characters}). Please upgrade your plan to create more characters.`,
            success: false
          };
        }
      }

      const { data, error } = await supabase
        .from('characters')
        .insert({
          user_id: user.id,
          name: characterData.name,
          type: characterData.type,
          personality: characterData.personality,
          appearance: characterData.appearance,
          age: characterData.age,
          favorite_color: characterData.favorite_color,
          special_powers: characterData.special_powers
        })
        .select()
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to create character',
        success: false
      };
    }
  },

  // Update a character
  async updateCharacter(id: string, updates: Partial<Character>): Promise<ApiResponse<Character>> {
    try {
      const { data, error } = await supabase
        .from('characters')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to update character',
        success: false
      };
    }
  },

  // Delete a character
  async deleteCharacter(id: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('characters')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to delete character',
        success: false
      };
    }
  },

  // Toggle favorite status
  async toggleFavorite(id: string): Promise<ApiResponse<Character>> {
    try {
      // First get the current favorite status
      const { data: currentCharacter, error: fetchError } = await supabase
        .from('characters')
        .select('is_favorite')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      const { data, error } = await supabase
        .from('characters')
        .update({
          is_favorite: !currentCharacter.is_favorite,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to toggle favorite',
        success: false
      };
    }
  },

  // Get characters by type
  async getCharactersByType(type: string): Promise<ApiResponse<Character[]>> {
    try {
      const { data, error } = await supabase
        .from('characters')
        .select('*')
        .eq('type', type)
        .order('name', { ascending: true });

      if (error) throw error;

      return {
        data: data || [],
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch characters by type',
        success: false
      };
    }
  },

  // Get favorite characters
  async getFavoriteCharacters(): Promise<ApiResponse<Character[]>> {
    try {
      const { data, error } = await supabase
        .from('characters')
        .select('*')
        .eq('is_favorite', true)
        .order('name', { ascending: true });

      if (error) throw error;

      return {
        data: data || [],
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch favorite characters',
        success: false
      };
    }
  },

  // Search characters
  async searchCharacters(query: string): Promise<ApiResponse<Character[]>> {
    try {
      const { data, error } = await supabase
        .from('characters')
        .select('*')
        .or(`name.ilike.%${query}%,personality.ilike.%${query}%,appearance.ilike.%${query}%`)
        .order('name', { ascending: true });

      if (error) throw error;

      return {
        data: data || [],
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to search characters',
        success: false
      };
    }
  },

  // Upload character avatar
  async uploadAvatar(characterId: string, file: File): Promise<ApiResponse<string>> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const fileExt = file.name.split('.').pop();
      const fileName = `${characterId}-${Date.now()}.${fileExt}`;
      const filePath = `characters/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update character with avatar URL
      const { error: updateError } = await supabase
        .from('characters')
        .update({
          avatar_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', characterId);

      if (updateError) throw updateError;

      return {
        data: publicUrl,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to upload avatar',
        success: false
      };
    }
  },

  // Get characters used in a story
  async getStoryCharacters(storyId: string): Promise<ApiResponse<(Character & { role: string })[]>> {
    try {
      const { data, error } = await supabase
        .from('story_characters')
        .select(`
          character_id,
          role,
          characters (*)
        `)
        .eq('story_id', storyId);

      if (error) throw error;

      const characters = (data?.map(item => ({
        ...(item.characters as Character),
        role: item.role
      })) || []) as (Character & { role: string })[];

      return {
        data: characters,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch story characters',
        success: false
      };
    }
  },

  // Add character to story
  async addCharacterToStory(storyId: string, characterId: string, role: string = 'main'): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('story_characters')
        .insert({
          story_id: storyId,
          character_id: characterId,
          role
        });

      if (error) throw error;

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to add character to story',
        success: false
      };
    }
  },

  // Remove character from story
  async removeCharacterFromStory(storyId: string, characterId: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('story_characters')
        .delete()
        .eq('story_id', storyId)
        .eq('character_id', characterId);

      if (error) throw error;

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to remove character from story',
        success: false
      };
    }
  }
}; 